import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout, { Breadcrumb } from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { articleCategories, articles } from "@/mocks/articles";
import { comicApps } from "@/mocks/apps";

export default function Reviews() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") ?? "");
  const active = params.get("cat") ?? "全部";

  const list = useMemo(() => {
    let result = articles;
    if (active !== "全部") result = result.filter((a) => a.category === active);
    if (query.trim()) {
      const kw = query.trim();
      result = result.filter((a) => a.title.includes(kw) || a.excerpt.includes(kw));
    }
    return result;
  }, [active, query]);

  usePageMeta({
    title: "漫画软件应用评测推荐 - 好用应用一网打尽",
    description:
      "精选漫画软件应用评测与推荐，覆盖排行盘点、免费推荐、正版指南与追更技巧，帮你快速锁定好用的漫画软件应用。",
    canonical: "https://mangao0.github.io/reviews",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "漫画软件应用评测推荐",
      description: "漫画软件应用评测文章合集",
      url: "https://mangao0.github.io/reviews",
    },
  });

  const setCat = (c: string) => {
    const next = new URLSearchParams(params);
    if (c === "全部") next.delete("cat");
    else next.set("cat", c);
    setParams(next);
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (query.trim()) next.set("q", query.trim());
    else next.delete("q");
    setParams(next);
  };

  return (
    <Layout title="评测推荐">
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "评测推荐" }]} />

      <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-2">
            <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">漫画软件应用评测推荐</h1>
            <p className="text-sm text-foreground-600">排行盘点、免费推荐、正版指南与追更技巧，为你持续输出实用内容。</p>
          </div>
          <form onSubmit={onSearch} className="flex w-full max-w-sm items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5">
            <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索评测文章…"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              aria-label="搜索评测文章"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
              搜索
            </button>
          </form>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 rounded-full border border-background-200 bg-background-100 p-1">
          {articleCategories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === c ? "bg-primary-500 font-bold text-background-50" : "text-foreground-700 hover:bg-background-200/70"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-background-200 bg-background-100 p-10 text-center text-sm text-foreground-600">
            没有找到相关评测，换个关键词试试吧。
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <article key={a.id} className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/80 bg-background-100 transition-transform duration-300 hover:-translate-y-1">
                <Link to={`/reviews/${a.id}`} className="block overflow-hidden">
                  <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
                    <img src={a.cover} alt={a.title} title={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                </Link>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">{a.category}</span>
                    <span>{a.date}</span>
                    <span>·</span>
                    <span>{a.readTime}</span>
                  </div>
                  <h2 className="mt-3 line-clamp-2 text-base font-bold text-foreground-950">
                    <Link to={`/reviews/${a.id}`} className="transition-colors hover:text-primary-600">{a.title}</Link>
                  </h2>
                  <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-700">{a.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-foreground-500">作者：{a.author}</span>
                    <Link to={`/reviews/${a.id}`} className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-bold text-primary-600 transition-colors hover:text-primary-700">
                      阅读全文
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-12 rounded-2xl border border-background-200 bg-background-100 p-6">
          <h2 className="text-base font-bold text-foreground-950">正在上榜的漫画软件应用</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {comicApps.map((a) => (
              <Link
                key={a.id}
                to={`/reviews/${a.id}`}
                className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                {a.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}